// Library Methods
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import ReportVersion from "./ReportVersion/ReportVersion";
import ReportPatientHeader from "./ReportPatientHeader/ReportPatientHeader";
import ReportCommentList from "./ReportCommentList/ReportCommentList";
import ReportColumns from "./ReportColumns/ReportColumns";
import ReportGraph from "./ReportGraph/ReportGraph";
import ReportError from "../ReportError/ReportError";

// Contexts
import { ReportSectionContext } from "src/contexts/reports/ReportSectionContext";
import { ReportContext } from "src/contexts/reports/ReportContext";

// Constants
import { ExamCardSectionNameType } from "src/constants/enums";

// Utils
import { isMultiExamPackage } from "src/utils/exams/examReportsHelper";

type ReportSectionType = {
  sectionName: ExamCardSectionNameType;
  groupId: number;
};

const ReportSections = ({ sectionName, groupId }: ReportSectionType) => {
  const { t } = useTranslation();
  const { reportData, exam } = useContext(ReportContext);

  const {
    sectionId,
    sectionId2,
    examReport,
    examReport2,
    eyeType,
    eyeType2,
    duration,
    duration2,
    durationStartTime,
    durationStartTime2,
    commentData,
    commentData2,
  } = reportData?.[sectionName]?.[groupId] ?? {};

  const isMultiPackage = useMemo(
    () => Boolean(exam && isMultiExamPackage(exam)),
    [exam]
  );

  const sectionClassName = useMemo(
    () => (isMultiPackage ? "retinalogik-package" : undefined),
    [isMultiPackage]
  );

  return (
    <Grid
      className={sectionClassName}
      container
      spacing="16px"
      sx={{ display: "flex", minWidth: "100%" }}
    >
      {examReport &&
        sectionId &&
        eyeType &&
        duration &&
        commentData &&
        durationStartTime && (
          <ReportSectionContext.Provider
            value={{
              sectionId,
              examReport,
              eyeType,
              durationStartTime,
              duration,
              commentData,
            }}
          >
            <Grid
              item
              xs={12}
              lg={6}
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                gap: "16px",
              }}
              className="retinalogik-section retinalogik-report"
              id="report-section-1"
              data-testid="report-section-1"
            >
              <ReportPatientHeader />
              <ReportColumns sectionName={sectionName} groupId={groupId} />

              {examReport?.postProcessing ? (
                <>
                  <Box sx={{ height: "fit-content" }}>
                    <ReportGraph sectionName={sectionName} />
                  </Box>
                  {commentData?.length > 0 && <ReportCommentList />}
                  <ReportVersion />
                </>
              ) : (
                <ReportError
                  title={t("Post-Processing Data is Not Available.")}
                />
              )}
            </Grid>
          </ReportSectionContext.Provider>
        )}
      {examReport2 &&
        sectionId2 &&
        eyeType2 &&
        duration2 &&
        commentData2 &&
        durationStartTime2 && (
          <ReportSectionContext.Provider
            value={{
              sectionId: sectionId2,
              examReport: examReport2,
              eyeType: eyeType2,
              durationStartTime: durationStartTime2,
              duration: duration2,
              commentData: commentData2,
            }}
          >
            <Grid
              item
              xs={12}
              lg={6}
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                gap: "16px",
              }}
              className="retinalogik-section retinalogik-report"
              id="report-section-2"
              data-testid="report-section-2"
            >
              <ReportPatientHeader />
              <ReportColumns sectionName={sectionName} groupId={groupId} />

              {examReport2?.postProcessing ? (
                <>
                  <Box sx={{ height: "fit-content" }}>
                    <ReportGraph sectionName={sectionName} />
                  </Box>
                  {commentData2?.length > 0 && <ReportCommentList />}
                  <ReportVersion />
                </>
              ) : (
                <ReportError
                  title={t("Post-Processing Data is Not Available.")}
                />
              )}
            </Grid>
          </ReportSectionContext.Provider>
        )}
    </Grid>
  );
};

export default ReportSections;
